import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, IconButton, useTheme } from '@mui/material';
import useProjectStyles from './useProjectStyles';
import { Delete24Regular, Edit24Regular } from '@fluentui/react-icons';
import { formatDMY } from '@application/utilities/date-time-formater';
import { SDataGrid2 } from '@ui/components/common/grid/SDataGrid2';
import ConfirmDialog from '@ui/components/Dialogs/ConfirmDialog';
import { ProjectApi } from '@application/api/endpoints/project-api';
import { toast } from 'react-toastify';
import { refreshProjects } from '@application/redux/Refresh/refreshSlice';
import { useDispatch } from 'react-redux';
const ProjectDataGrid = ({ fullHeight = true, rows, hiddenColumns = [], showFilters, filters, setFilters, onEdit, setRefresh, projectQuery, setProjectQuery, totalRecords }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { projectsGridCardStyles } = useProjectStyles();
    const [deleteProjectId, setDeleteProjectId] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const handleConfirmDelete = async () => {
        try {
            if (deleteProjectId) {
                const response = await ProjectApi.deleteProjectbyId(deleteProjectId);
                if (response?.isSuccess) {
                    toast.success(response?.message);
                    dispatch(refreshProjects());
                }
                else {
                    toast.error(response?.message);
                }
            }
        }
        catch (error) {
            toast.error(t('FailedNotesError'));
        }
        finally {
            setIsDialogOpen(false);
            setRefresh(true);
        }
    };
    const columns = [
        {
            field: 'projectName',
            headerName: t('Project Name'),
            minWidth: 150,
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (params) => {
                const { row } = params;
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [row.imageName ? (_jsx("img", { src: `${process.env.BLOB_BASE_URL}${process.env.FOLDER_NAME}/ProjectLogo/${row.imageName}`, alt: 'logo', width: 22, height: 24, style: { objectFit: 'contain', borderRadius: '2px' } })) : (_jsx(Box, { sx: {
                                width: 22,
                                height: 24,
                                backgroundColor: theme.palette.action.disabled,
                                borderRadius: '2px'
                            } })), _jsx("span", { children: row.projectName })] }));
            }
        },
        {
            field: 'startDate',
            headerName: t('StartDate'),
            minWidth: 150,
            flex: 1,
            type: 'date',
            valueGetter: (params) => new Date(params),
            valueFormatter: (params) => formatDMY(params),
            editable: false
        },
        {
            field: 'endDate',
            headerName: t('End Date'),
            minWidth: 150,
            type: 'date',
            flex: 1,
            valueGetter: (params) => new Date(params),
            valueFormatter: (params) => formatDMY(params),
            editable: false
        },
        {
            field: 'description',
            headerName: t('Description'),
            minWidth: 300,
            flex: 1.5,
            editable: false
        },
        {
            field: '',
            headerName: '',
            minWidth: 150,
            display: 'flex',
            align: 'center',
            editable: false,
            sortable: false,
            type: 'actions',
            renderCell: (params) => {
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '12px', px: 2 }, children: [_jsx(IconButton, { size: 'small', children: _jsx(Edit24Regular, { color: theme.palette.grey[600], onClick: () => onEdit?.(params.row.id) }) }), _jsx(IconButton, { size: 'small', color: 'error', onClick: () => {
                                setDeleteProjectId(params.row.id);
                                setIsDialogOpen(true);
                            }, children: _jsx(Delete24Regular, {}) })] }));
            }
        }
    ];
    return (_jsxs(Fragment, { children: [_jsx(Card, { className: 'projects-grid-card', sx: projectsGridCardStyles, children: _jsx(Box, { className: 'table-container', height: fullHeight ? 'calc(100dvh - 180px)' : 'auto', children: _jsx(SDataGrid2, { rows: rows, columns: columns, serverSidePagination: true, loading: false, showFilters: showFilters, hideColumns: hiddenColumns, filters: filters, setFilters: setFilters, initialState: {
                            pagination: {
                                paginationModel: {
                                    page: (projectQuery.pageNumber || 1) - 1,
                                    pageSize: projectQuery.pageSize || 10
                                }
                            }
                        }, onPaginationModelChange: (model) => {
                            setProjectQuery((prev) => ({
                                ...prev,
                                pageNumber: model.page + 1,
                                pageSize: model.pageSize
                            }));
                        }, totalRows: totalRecords }) }) }), _jsx(ConfirmDialog, { isOpen: isDialogOpen, onClose: () => setIsDialogOpen(false), onConfirm: handleConfirmDelete, actionText: t('Delete'), screenName: t('Project'), variant: 'delete' })] }));
};
export default ProjectDataGrid;
